import * as fs from 'node:fs';
import * as path from 'node:path';
import type { Command } from 'commander';
import { setCliLogVerbosity } from '../cli-log-level.js';
import { readConfigForApi } from '../config.js';
import { loadOrCreateE2eCertificates } from '../e2e-certificates/certificates.js';
import { getBridgeRoot, initBridgeRoot } from '../files/cwd/index.js';
import { runBridge } from '../run-bridge.js';
import type { CliOptions } from './cli-options.js';
import { DEFAULT_API_URL, DEFAULT_FIREHOSE_URL } from './defaults.js';

const GOLDEN_YELLOW = '\x1b[38;5;220m';
const REDDISH_ORANGE = '\x1b[38;5;208m';
const RESET = '\x1b[0m';
const E2EE_CERTIFICATES_OPTION = '--e2ee-certificates-dir <path>';

function colorize(color: string, message: string): string {
  return `${color}${message}${RESET}`;
}

export async function runCliAction(program: Command, opts: CliOptions): Promise<void> {
  const trace = opts.trace === true;
  const debug = opts.debug === true || trace;
  setCliLogVerbosity(trace ? 'trace' : debug ? 'debug' : 'info');

  // Allow API URL as first positional (e.g. npm run start -- https://api.example.com)
  const positionalUrl = program.args?.[0];
  const urlFromPositional =
    typeof positionalUrl === 'string' && /^https?:\/\//i.test(positionalUrl)
      ? positionalUrl
      : undefined;
  const apiUrlFromCli = opts.apiUrl ?? urlFromPositional;
  const apiUrl = apiUrlFromCli ?? DEFAULT_API_URL;
  let workspaceId = opts.workspaceId ?? '';
  let authToken = opts.token;
  const firehoseServerUrl =
    opts.firehoseUrl ??
    opts.proxyUrl ??
    process.env.BUILDAUTOMATON_FIREHOSE_URL ??
    process.env.BUILDAUTOMATON_PROXY_URL ??
    DEFAULT_FIREHOSE_URL;

  const bridgeRootOpt =
    (opts.bridgeRoot && typeof opts.bridgeRoot === 'string' && opts.bridgeRoot.trim()
      ? opts.bridgeRoot.trim()
      : null) ??
    (opts.cwd && typeof opts.cwd === 'string' && opts.cwd.trim() ? opts.cwd.trim() : null);
  if (bridgeRootOpt) {
    const resolvedBridgeRoot = path.resolve(process.cwd(), bridgeRootOpt);
    try {
      const st = fs.statSync(resolvedBridgeRoot);
      if (!st.isDirectory()) {
        console.error(`Bridge root is not a directory: ${resolvedBridgeRoot}`);
        process.exit(1);
      }
    } catch {
      console.error(`Bridge root path does not exist or is not accessible: ${resolvedBridgeRoot}`);
      process.exit(1);
    }
    process.chdir(resolvedBridgeRoot);
  }

  initBridgeRoot();
  console.log(
    colorize(
      GOLDEN_YELLOW,
      `[Bridge] Using ${getBridgeRoot()} as the bridge root (repos, file index, agents, and bridge_root sessions).`,
    ),
  );

  let worktreesRootPath: string | undefined;
  if (opts.worktreesRoot && opts.worktreesRoot.trim()) {
    worktreesRootPath = path.resolve(opts.worktreesRoot.trim());
  }

  const e2eCertificates = opts.e2eeCertificatesDir?.trim()
    ? await loadOrCreateE2eCertificates(opts.e2eeCertificatesDir.trim())
    : undefined;
  if (e2eCertificates) {
    const action = e2eCertificates.generated ? 'Generated' : 'Loaded';
    console.log(
      `[E2EE] ${action} ${e2eCertificates.certificates.length} key${e2eCertificates.certificates.length === 1 ? '' : 's'} from ${e2eCertificates.directory}`,
    );
    console.log(
      `[E2EE] Active key: ${e2eCertificates.activeCertificate.name} (${e2eCertificates.activeCertificate.id})`,
    );
  } else {
    console.warn(
      colorize(
        REDDISH_ORANGE,
        `[E2EE] Warning: this CLI session is not end-to-end encrypted. Start with ${E2EE_CERTIFICATES_OPTION} to enable end-to-end encryption.`,
      ),
    );
  }

  let refreshToken: string | undefined;
  if ((!workspaceId || !authToken) && opts.config !== false) {
    const saved = readConfigForApi(apiUrl);
    if (saved?.workspaceId && saved?.token) {
      if (!workspaceId) workspaceId = saved.workspaceId;
      if (!authToken) authToken = saved.token;
      refreshToken = saved.refreshToken;
    }
  }

  await runBridge({
    apiUrl,
    workspaceId: workspaceId || undefined,
    authToken,
    refreshToken,
    firehoseServerUrl,
    bridgeName: opts.name?.trim() || undefined,
    worktreesRootPath,
    e2eCertificate: e2eCertificates?.activeCertificate,
  });
}
